// T-072: the page scripts under panel/js were written for sweetalert2, select2, DataTables and the old Jalali date
// picker. On a page moved to the design system those libraries are not loaded; this file answers the same calls with
// the design system's own parts (toast, select, table, date picker), so the page logic (AJAX, validation, the values
// it posts) stays exactly as it was. Classic script: after jQuery and main-14040620.js, before the page scripts.
// `var`, plain functions, `arguments` and the old-browser checks are kept on purpose: it patches jQuery's own functions.
/* eslint-disable no-var, prefer-rest-params, @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unsafe-function-type */
import type { DsApi } from '@soren/ui/scripts/ds/index.ts';
import type { EditorTextarea } from '@soren/ui/scripts/ds/editor.ts';
import type { EnhancedSelect, RemoteItem, SelectConfig } from '@soren/ui/scripts/ds/select.ts';
import type { ToastOptions, ToastType } from '@soren/ui/scripts/ds/toast.ts';
import type { DsQueued } from '@soren/ui/scripts/globals.d.ts';

/** A jQuery set, as far as this bridge uses it (jQuery 3; the page scripts load it). */
interface JQuerySet {
  each(fn: (this: HTMLElement, i: number) => void): JQuerySet;
  first(): { val(): unknown };
  [n: number]: HTMLElement;
}
/** jQuery's plugin table ($.fn), with the plugins this bridge answers. */
interface JQueryFn {
  select2: (this: JQuerySet, opt?: string | Select2Options) => JQuerySet;
  DataTable: (this: JQuerySet) => DataTableApi;
  dataTable: (this: JQuerySet) => DataTableApi;
  summernote: (this: JQuerySet, cmd?: string, value?: string) => unknown;
  val: Function;
  text: Function;
  html: Function;
}
/** jQuery itself: $(x), $.ajax, $.fn. */
interface JQueryStatic {
  (selector?: unknown): JQuerySet & { on(ev: string, sel: string, fn: (this: EnhancedSelect) => void): void };
  fn: JQueryFn;
  ajax(settings: {
    url: string;
    type: string;
    dataType: string;
    contentType?: string;
    data: unknown;
    success: (r: unknown) => void;
    error: () => void;
  }): void;
}
/** select2's options, the part pages use. */
interface Select2Options {
  placeholder?: string;
  allowClear?: boolean;
  minimumInputLength?: number;
  ajax?: {
    url: string | ((params: Select2Params) => string);
    type?: string;
    dataType?: string;
    contentType?: string;
    data?: unknown;
    processResults?: (r: unknown, params: Select2Params) => { results?: Select2Item[] } | undefined;
  };
}
interface Select2Params {
  term?: string;
}
interface Select2Item {
  id?: string | number | null;
  text?: string | null;
}
/** What $(table).DataTable() returns: the few calls pages make, all answered. */
interface DataTableApi {
  row(): { data(): null; nodes(): { to$(): JQuerySet } };
  rows(): { data(): never[]; nodes(): never[] };
  on(): DataTableApi;
  draw(): DataTableApi;
  destroy(): DataTableApi;
  clear(): DataTableApi;
}
/** sweetAlert's argument object. */
interface SweetOptions {
  title?: string;
  text?: string;
  html?: string;
  icon?: string;
  type?: string;
}
type Sweet = (a?: string | SweetOptions, b?: string, c?: string) => Promise<{ isConfirmed: boolean; value: boolean }>;

declare global {
  interface Window {
    sweetAlert: Sweet;
    swal: Sweet;
    Swal: { fire: Sweet; close(): void; showLoading(): void; isVisible(): boolean };
    dsAsk: (text: string) => Promise<boolean>;
    jalaliDatepicker: { startWatch(): void; updateOptions(): void; show(): void; hide(): void };
  }
}

(function ($: JQueryStatic | undefined) {
  'use strict';
  var queue = (window.dsQueue = window.dsQueue || []) as DsQueued[];
  function withDs(fn: DsQueued): void {
    if (window.ds && !Array.isArray(window.dsQueue)) fn(window.ds);
    else queue.push(fn);
  }
  function plain(html: unknown): string {
    var d = document.createElement('div');
    d.innerHTML = String(html == null ? '' : html);
    return (d.textContent || '').trim();
  }

  // sweetAlert(title, text, icon) / sweetAlert({ title, text, icon }) -> a toast; resolves like a confirmed alert
  var TYPE: Record<string, ToastType | undefined> = {
    error: 'danger',
    warning: 'warning',
    info: 'info',
    question: 'info',
    success: 'success',
  };
  function sweet(a?: string | SweetOptions, b?: string, c?: string): Promise<{ isConfirmed: boolean; value: boolean }> {
    var title: string | undefined, text: string | undefined, kind: string | undefined;
    if (a && typeof a === 'object') {
      title = a.title;
      text = a.text || a.html;
      kind = a.icon || a.type;
    } else {
      title = a;
      text = b;
      kind = c;
    }
    title = plain(title);
    text = plain(text);
    var opt: ToastOptions = { type: TYPE[kind!] || 'success' };
    if (title && text) opt.title = title;
    // an error kept sweetalert's OK button: pages bind $('.swal2-confirm').on('click', …) right after (login reloads)
    if (opt.type === 'danger') opt.action = { label: 'باشه', className: 'swal2-confirm', run: function () {} };
    var msg = text || title;
    if (msg)
      withDs(function (ds) {
        ds.toast(msg, opt);
        if (opt.type !== 'danger') ds.flash(msg, { type: opt.type, title: opt.title });
      });
    return Promise.resolve({ isConfirmed: true, value: true });
  }
  window.sweetAlert = window.swal = sweet;
  window.Swal = {
    fire: sweet,
    close: function () {},
    showLoading: function () {},
    isVisible: function () {
      return false;
    },
  };

  // dsAsk(text) -> Promise<boolean>: the design system's confirm in place of window.confirm (page scripts call it as
  // `dsAsk("…").then(function (result) { … })`); a delete question gets the red dialog
  window.dsAsk = function (text) {
    var del = /حذف/.test(text);
    return new Promise(function (resolve) {
      withDs(function (ds) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- resolved through resolve()
        ds.confirm({ title: text, ok: del ? 'حذف' : 'بله، انجام شود', danger: del }).then(resolve);
      });
    });
  };

  if (!$) return;

  // select2 -> the design system select (local list, or remote with the page's own ajax settings)
  $.fn.select2 = function (opt) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- jQuery's set, used by the .each() callbacks below
    var els = this;
    if (typeof opt === 'string') {
      els.each(function () {
        var el = this as EnhancedSelect;
        withDs(function () {
          var c = el._ds;
          if (!c) return;
          if (opt === 'open') c.open();
          else if (opt === 'close' || opt === 'destroy') c.close();
        });
      });
      return els;
    }
    opt = opt || {};
    els.each(function () {
      var el = this as EnhancedSelect,
        cfg: SelectConfig = { placeholder: opt.placeholder, clearable: !!opt.allowClear };
      if (opt.ajax && opt.ajax.url) {
        var a = opt.ajax;
        cfg.minChars = opt.minimumInputLength || 0;
        cfg.remote = function (term) {
          return new Promise<RemoteItem[]>(function (resolve) {
            // like select2: before anything is typed params.term is undefined (pages build "a&&&b&&&undefined" from it)
            var params: Select2Params = term ? { term: term } : {};
            $.ajax({
              url: typeof a.url === 'function' ? a.url(params) : a.url,
              type: a.type || 'GET',
              dataType: a.dataType || 'json',
              contentType: a.contentType,
              data: typeof a.data === 'function' ? (a.data as (p: Select2Params) => unknown)(params) : a.data || params,
              success: function (r) {
                var list =
                  typeof a.processResults === 'function'
                    ? (a.processResults(r, params) || {}).results
                    : (r as Select2Item[] | null);
                resolve(
                  (list || [])
                    .filter(function (x) {
                      return x && x.id !== undefined && x.id !== null;
                    })
                    .map(function (x) {
                      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- the value can arrive as a string from JSON or a data-* attribute
                      return { value: String(x.id), label: x.text != null ? String(x.text) : String(x.id) };
                    }),
                );
              },
              error: function () {
                resolve([]);
              },
            });
          });
        };
      }
      withDs(function (ds) {
        ds.enhanceSelect(el, cfg);
      });
    });
    return els;
  };

  // DataTables: $(table).DataTable() -> the design system table; the returned object answers the few calls pages make
  function api(els: JQuerySet): DataTableApi {
    var self: DataTableApi = {
      row: function () {
        return {
          data: function () {
            return null;
          },
          nodes: function () {
            return {
              to$: function () {
                return $!();
              },
            };
          },
        };
      },
      rows: function () {
        return {
          data: function () {
            return [];
          },
          nodes: function () {
            return [];
          },
        };
      },
      on: function () {
        return self;
      },
      draw: function () {
        els.each(function () {
          var t = this;
          withDs(function (ds) {
            ds.table(t);
          });
        });
        return self;
      },
      destroy: function () {
        return self;
      },
      clear: function () {
        return self;
      },
    };
    return self;
  }
  $.fn.DataTable = $.fn.dataTable = function () {
    this.each(function () {
      var t = this;
      if (t.tagName === 'TABLE')
        withDs(function (ds) {
          ds.table(t);
        });
    });
    return api(this);
  };

  // Summernote -> the design system's editor ('code' reads/writes the HTML, as before)
  $.fn.summernote = function (cmd, value) {
    if (cmd === 'code') {
      if (value === undefined) return this.first().val();
      this.each(function () {
        var el = this as EditorTextarea;
        if (el._dsEditor) el._dsEditor.set(value);
        else el.value = value;
      });
      return this;
    }
    this.each(function () {
      var el = this as EditorTextarea;
      if (el.tagName === 'TEXTAREA')
        withDs(function (ds: DsApi) {
          ds.enhanceEditor(el);
        });
    });
    return this;
  };

  // the old picker: data-jdp inputs are now the design system's Jalali picker (ds.js), also when added later
  window.jalaliDatepicker = {
    startWatch: function () {},
    updateOptions: function () {},
    show: function () {},
    hide: function () {},
  };

  // a value set by script ($(select).val(x) or .trigger('change')) shows in the design system select
  var val = $.fn.val;
  $.fn.val = function (this: JQuerySet) {
    var r: unknown = val.apply(this, arguments);
    if (arguments.length)
      this.each(function () {
        if (this.tagName === 'SELECT' && (this as EnhancedSelect)._ds) (this as EnhancedSelect)._ds!.refresh();
      });
    return r;
  };
  $(document).on('change', 'select', function () {
    if (this._ds) this._ds.refresh();
  });

  // A button's label set with .text() («در حال انجام…», «ثبت شد») keeps the button's design system icon in front
  var text = $.fn.text;
  $.fn.text = function (this: JQuerySet, v?: unknown) {
    if (!arguments.length || v === undefined || typeof v === 'function') return text.apply(this, arguments) as unknown;
    return this.each(function () {
      var ic = this.classList && this.classList.contains('btn') ? this.querySelector(':scope > svg.ds-ico') : null;
      text.call($(this), v);
      if (ic && !this.classList.contains('btn-icon')) this.insertBefore(ic, this.firstChild);
    });
  };

  // HTML that page scripts insert with .html()/.append() gets the design system's parts
  var html = $.fn.html;
  $.fn.html = function (this: JQuerySet, v?: unknown) {
    var r: unknown = html.apply(this, arguments);
    if (arguments.length && v !== undefined)
      this.each(function () {
        var el = this;
        withDs(function (ds) {
          ds.enhance(el);
        });
      });
    return r;
  };
})(window.jQuery as unknown as JQueryStatic | undefined);
